import React from 'react';
import { Calendar } from 'lucide-react';

type Preset =
  | 'This Month'
  | 'Last 30 Days'
  | 'Last 90 Days'
  | 'This Quarter'
  | 'This Year'
  | 'Year to Date'
  | 'All Time';

export interface DateRange {
  from?: Date;
  to?: Date;
  label: string;
}

interface Props {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

const presets: Preset[] = [
  'This Month',
  'Last 30 Days',
  'Last 90 Days',
  'This Quarter',
  'This Year',
  'Year to Date',
  'All Time',
];

function startOfQuarter(date: Date) {
  const month = date.getMonth();
  const qStartMonth = Math.floor(month / 3) * 3;
  return new Date(date.getFullYear(), qStartMonth, 1);
}

function endOfQuarter(date: Date) {
  const start = startOfQuarter(date);
  return new Date(start.getFullYear(), start.getMonth() + 3, 0);
}

function formatISODate(d: Date) {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  return dt.toISOString().slice(0, 10);
}

const DateRangeFilter: React.FC<Props> = ({ value, onChange }) => {
  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selected = e.target.value as Preset;

    let from: Date | undefined;
    let to: Date | undefined;
    let label = selected;

    switch (selected) {
      case 'This Month': {
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      }
      case 'Last 30 Days': {
        to = today;
        from = new Date(today);
        from.setDate(from.getDate() - 30);
        break;
      }
      case 'Last 90 Days': {
        to = today;
        from = new Date(today);
        from.setDate(from.getDate() - 90);
        break;
      }
      case 'This Quarter': {
        from = startOfQuarter(today);
        to = endOfQuarter(today);
        break;
      }
      case 'This Year': {
        from = new Date(today.getFullYear(), 0, 1);
        to = new Date(today.getFullYear(), 11, 31);
        break;
      }
      case 'Year to Date': {
        from = new Date(today.getFullYear(), 0, 1);
        to = today;
        break;
      }
      case 'All Time': {
        from = undefined;
        to = undefined;
        break;
      }
    }

    onChange({ from, to, label });
  };

  return (
    <div className="flex items-center gap-2">
      <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
      <select
        className="h-9 px-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-300"
        onChange={handleSelect}
        value={value.label}
      >
        {presets.map(p => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
    </div>
  );
};

export default DateRangeFilter;

